import React, { useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks';
// Deep-import path on purpose: the top-level `../../ui` barrel (and its
// `./components` barrel) re-exports `./Mapbox`, which eagerly requires
// `mapbox-gl` — a module that throws in jsdom. Importing straight from
// `src/ui/components/Select` (and `.../MenuItem`) skips that barrel chain
// entirely, so the real design-system control can be used here without
// dragging Mapbox into this page's module graph. CellMap.jsx (the sibling map
// component under QuoteGrids) documents the same Mapbox-barrel hazard.
import { Select } from '../../ui/components/Select';
import { MenuItem } from '../../ui/components/MenuItem';
import SnapshotStats from './SnapshotStats';
import ConcernRegister from './ConcernRegister';
import ClientMovement from './ClientMovement';
import HexMap from './HexMap';
import MapLegend from './MapLegend';
import CellDetailDrawer from './CellDetailDrawer';
import { peakOf, METRICS } from './hexMapStyles';

const DEFAULT_SCALE_MI = 25;
const DEFAULT_METRIC = 'pml';

const listingLabel = ({ asOf, redCount = 0, amberCount = 0 }) => `${asOf} · ${redCount} red / ${amberCount} amber`;

// Cache transitions for cellsByScaleRef, exported as pure helpers so the
// retry/in-flight/success contract can be unit-tested without needing a UI
// control to drive scaleMi (that lands with Task 13's HexMap scale toggle).
export function shouldFetchScale(cache, scaleMi) {
  return cache[scaleMi] === undefined;
}

export function markFetching(cache, scaleMi) {
  return { ...cache, [scaleMi]: null };
}

export function markFetched(cache, scaleMi, cells) {
  return { ...cache, [scaleMi]: cells };
}

// Used both when a fetch is cancelled (superseded by a newer asOf/scaleMi)
// and when it rejects: removing the key (rather than leaving the `null`
// in-flight sentinel in place) means a later re-visit of this scaleMi will
// retry instead of being stuck behind the `!== undefined` guard forever.
export function clearFetch(cache, scaleMi) {
  if (!(scaleMi in cache)) return cache;
  const next = { ...cache };
  delete next[scaleMi];
  return next;
}

// `exec.reinsurance.worst_occ_pml_250` (the cession-headroom numerator, per the
// artifact contract) may not be populated on every summary payload yet. When
// it's missing, SnapshotStats falls back to the largest `pml250Occ` this page
// already has in hand across the concerns + top10 arrays. Exported as a pure
// helper so the fallback math is unit-testable without mounting the page.
export function worstOccFromCells(cells) {
  const values = (cells || [])
    .map((c) => c?.pml250Occ)
    .filter((v) => v !== null && v !== undefined && !Number.isNaN(Number(v)))
    .map(Number);
  return values.length ? Math.max(...values) : null;
}

// Plain markup (no `src/ui` primitives) elsewhere on this page on purpose —
// see the Select/MenuItem import comment above for why those two are the
// exception. The rest keeps the div/style convention CellMap.jsx also uses.
const S = {
  page: { display: 'flex', flexDirection: 'column', gap: 16, padding: 16 },
  header: { display: 'flex', alignItems: 'center', gap: 12 },
  title: { fontWeight: 600, fontSize: 20 },
  stats: { display: 'flex', flexDirection: 'column', gap: 4 },
  // Map spans the full page width; the tables sit below it in a tab strip.
  mapSection: { position: 'relative', display: 'flex', flexDirection: 'column', gap: 8, width: '100%' },
  // Fixed height so HexMap's height:100% (and its flex:1 map area) resolve to a
  // real size now that the map is no longer stretched by a flex-row sibling.
  mapFrame: { height: 560 },
  tabRow: { display: 'flex', gap: 4, borderBottom: '1px solid #DFE3E8', marginTop: 8 },
  tab: {
    border: 'none',
    background: 'transparent',
    padding: '8px 14px',
    fontSize: 13,
    cursor: 'pointer',
    color: '#637381',
    borderBottom: '2px solid transparent',
  },
  tabActive: { color: '#212B36', fontWeight: 600, borderBottom: '2px solid #5F2994' },
  tabPanel: { paddingTop: 12 },
  empty: { padding: 24, color: '#637381' },
  error: { color: '#b71c1c', fontWeight: 500 },
};

const TABS = [
  { key: 'concern', label: 'Areas of concern' },
  { key: 'top10', label: 'Top 10 cells' },
  { key: 'clients', label: 'Client movement' },
];

// Task 11: page shell — owns the month picker + the data-flow state contract that
// Tasks 12/13 (SnapshotStats, ConcernRegister, HexMap, CellDetailDrawer, ClientMovement)
// build against. Those children don't exist yet; sections below render the fetched
// data minimally until they're mounted.
const PmlSnapshotPage = () => {
  const { getPmlSnapshots, getPmlSnapshotSummary, getPmlSnapshotCells, getPmlSnapshotClients } = useApi();

  // `listings` is null while the initial getPmlSnapshots call is in flight, and an
  // (possibly empty) array once it resolves — that distinction is what picks
  // loading vs. empty-state vs. loaded below.
  const [listings, setListings] = useState(null);
  const [asOf, setAsOf] = useState(null);
  const [summary, setSummary] = useState(null);
  const [scaleMi, setScaleMi] = useState(DEFAULT_SCALE_MI);
  const [metric, setMetric] = useState(DEFAULT_METRIC);
  const [cellsByScale, setCellsByScale] = useState({});
  const [openCell, setOpenCell] = useState(null);
  // Task 13's HexMap/CellDetailDrawer will read this to pan/highlight the clicked
  // cell; set alongside `openCell` (never on its own) so both always agree on
  // which cell is selected.
  const [focusCell, setFocusCell] = useState(null);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState(null);
  // Which table tab is showing under the map.
  const [tab, setTab] = useState('concern');

  // Mount: list snapshots, default to the latest (first) asOf. Guarded so React's
  // dev-mode double-invoke / any re-render never issues a second listing fetch.
  const listingsRequestedRef = useRef(false);
  useEffect(() => {
    if (listingsRequestedRef.current) return;
    listingsRequestedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const res = await getPmlSnapshots();
        if (cancelled) return;
        const snapshots = res?.snapshots || [];
        setListings(snapshots);
        if (snapshots.length) {
          setAsOf(snapshots[0].asOf);
        }
      } catch (e) {
        if (!cancelled) {
          setError('Failed to load PML snapshots.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // asOf change -> summary + client movement for that month. Clears any
  // stale error up front so switching months is itself a retry.
  useEffect(() => {
    if (!asOf) return;
    setError(null);
    let cancelled = false;
    (async () => {
      try {
        const [summaryRes, clientsRes] = await Promise.all([getPmlSnapshotSummary(asOf), getPmlSnapshotClients(asOf)]);
        if (cancelled) return;
        setSummary(summaryRes);
        setClients(clientsRes?.clients || []);
      } catch (e) {
        if (!cancelled) {
          setError('Failed to load the snapshot summary for this month.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asOf]);

  // cellsByScale is keyed by scaleMi and cached per asOf. cellsByScaleRef mirrors the
  // state synchronously (including an in-flight sentinel) so this single effect can
  // serve both triggers — an asOf change (which invalidates/resets the cache and
  // fetches the current scale) and a scaleMi change (which fetches only if that
  // scale isn't already cached for the current asOf) — without a duplicate fetch
  // when both fire around the same render.
  const cellsByScaleRef = useRef({});
  const cacheAsOfRef = useRef(null);
  useEffect(() => {
    if (!asOf) return;
    let cancelled = false;
    if (cacheAsOfRef.current !== asOf) {
      cacheAsOfRef.current = asOf;
      cellsByScaleRef.current = {};
      setCellsByScale({});
    }
    if (!shouldFetchScale(cellsByScaleRef.current, scaleMi)) return undefined;
    // Mark in-flight synchronously (before the await) so a concurrent run of this
    // same effect (e.g. triggered by the asOf reset above) can't also kick off a
    // fetch for the same scale.
    cellsByScaleRef.current = markFetching(cellsByScaleRef.current, scaleMi);
    (async () => {
      try {
        const cells = await getPmlSnapshotCells(asOf, scaleMi);
        if (cancelled) return;
        cellsByScaleRef.current = markFetched(cellsByScaleRef.current, scaleMi, cells);
        setCellsByScale(cellsByScaleRef.current);
      } catch (e) {
        if (!cancelled) {
          cellsByScaleRef.current = clearFetch(cellsByScaleRef.current, scaleMi);
          setCellsByScale(cellsByScaleRef.current);
          setError('Failed to load cells for this scale.');
        }
      }
    })();
    return () => {
      cancelled = true;
      // Superseded before the fetch settled (e.g. a rapid scale toggle) — free
      // the slot so a re-visit of this scaleMi retries instead of being stuck
      // behind the in-flight sentinel forever. Only clear it if it's still
      // ours (still `null`); if it already has data or was already cleared by
      // someone else, leave it alone.
      if (cellsByScaleRef.current[scaleMi] === null) {
        cellsByScaleRef.current = clearFetch(cellsByScaleRef.current, scaleMi);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asOf, scaleMi]);

  if (listings !== null && listings.length === 0) {
    return (
      <div data-testid="pml-snapshot-page">
        <div style={S.empty}>No snapshots loaded yet</div>
      </div>
    );
  }

  const cells = cellsByScale[scaleMi];
  const peak = peakOf(cells?.features, METRICS[metric]?.prop);
  const currentListing = listings?.find((l) => l.asOf === asOf) || null;
  const fallbackWorstOcc = worstOccFromCells([...(summary?.concerns || []), ...(summary?.top10 || [])]);

  // ConcernRegister rows (and, once Task 13 wires it, HexMap) call this with the
  // scale/cellId *of the clicked row*, which may differ from the page's current
  // scaleMi toggle — both `openCell` (drives the Task-13 CellDetailDrawer) and
  // `focusCell` (drives the Task-13 HexMap pan/highlight) are set together so
  // they never disagree about which cell is selected.
  const handleCellClick = (clickScaleMi, cellId) => {
    if (!cellId) {
      setOpenCell(null);
      setFocusCell(null);
      return;
    }
    setOpenCell({ scaleMi: clickScaleMi, cellId });
    setFocusCell({ scaleMi: clickScaleMi, cellId });
  };

  return (
    <div data-testid="pml-snapshot-page">
      <div style={S.page}>
        <div style={S.header}>
          <div style={S.title}>Monthly PML Snapshot</div>
          {listings && listings.length > 0 && (
            <Select value={asOf || ''} onChangeValue={(value) => setAsOf(value)}>
              {listings.map((listing) => (
                <MenuItem key={listing.asOf} value={listing.asOf}>
                  {listingLabel(listing)}
                </MenuItem>
              ))}
            </Select>
          )}
        </div>

        {error && <div style={S.error}>{error}</div>}

        {summary && (
          <div style={S.stats}>
            <SnapshotStats exec={summary.exec} listing={currentListing} fallbackWorstOcc={fallbackWorstOcc} />
          </div>
        )}

        <div style={S.mapSection}>
          <div style={S.mapFrame}>
            <HexMap
              cells={cells}
              metric={metric}
              scaleMi={scaleMi}
              onScaleChange={setScaleMi}
              onMetricChange={setMetric}
              onCellClick={handleCellClick}
              focusCell={focusCell}
            />
          </div>
          <MapLegend metric={metric} peak={peak} />
          <CellDetailDrawer asOf={asOf} openCell={openCell} onClose={() => setOpenCell(null)} />
        </div>

        <div>
          <div style={S.tabRow} role="tablist">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => setTab(t.key)}
                style={{ ...S.tab, ...(tab === t.key ? S.tabActive : {}) }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div style={S.tabPanel}>
            {(tab === 'concern' || tab === 'top10') && (
              <ConcernRegister
                section={tab === 'concern' ? 'register' : 'top10'}
                concerns={summary?.concerns}
                top10={summary?.top10}
                ceilingLadder={summary?.ceilingLadder}
                onCellClick={handleCellClick}
              />
            )}
            {tab === 'clients' && <ClientMovement clients={clients} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PmlSnapshotPage;
